import { useMemo, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import useDebounce from 'root/src/client/web/hooks/useDebounce';
import { Platform, DarePlatform } from 'root/src/shared/@types/platforms';
import { DarePlatformIcon, PlatformIconColor } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import { Input } from 'root/src/client/v2/common/components/ui/input';
import { Img } from 'root/src/client/v2/common/components/ui/image';
import { Popover, PopoverAnchor, PopoverContent } from 'root/src/client/v2/common/components/ui/popover';
import { Spinner } from 'root/src/client/v2/common/components/ui/spinner';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { PillSelect } from 'root/src/client/v2/_pages/add-campaign/redesign/components/PillGroup';
import { useCreatorSearch } from 'root/src/client/v2/_pages/drop/redesign/useCreatorSearch';
import { creatorOptionKey } from 'root/src/client/v2/_pages/drop/redesign/brandDare';
import { cn } from 'root/src/client/v2/common/helpers/cn';
// A creator platform is always one of the dare platforms, but they are separate enums (DarePlatform
// also covers YouTube Shorts and X, which nobody has an account "on"), so map rather than cast.
const DARE_PLATFORM = {
    [Platform.Twitch]: DarePlatform.Twitch,
    [Platform.YouTube]: DarePlatform.YouTube,
    [Platform.TikTok]: DarePlatform.TikTok,
};
// Same three platforms the legacy "Dare a Creator" form offered, in the same order. Plain data here,
// turned into pills (with the platform icon) at render time: building the JSX at module scope would
// need React in scope under the classic JSX runtime the jest transform uses.
const PLATFORM_PILLS = [
    { value: Platform.Twitch, label: 'Twitch' },
    { value: Platform.YouTube, label: 'YouTube' },
    { value: Platform.TikTok, label: 'TikTok' },
];
const platformPillOptions = () => PLATFORM_PILLS.map(pill => ({
    value: pill.value,
    label: (<span className="flex items-center gap-1.5">
			<DarePlatformIcon size="12" platform={DARE_PLATFORM[pill.value]} color={PlatformIconColor.Light}/>
			{pill.label}
		</span>),
}));
// YouTube search burns Google API quota per keystroke-batch, so it waits longer, exactly as the
// legacy picker did (youtubeSearchDebounce). The other two hit our own infrastructure.
const debounceForPlatform = (platform) => (platform === Platform.YouTube ? 500 : 250);
const CreatorAvatar = ({ src, alt, iconSize = 12 }) => {
    const [status, setStatus] = useState('loading');
    return (<>
			{(status === 'loading' || !src) && <Skeleton className="absolute inset-0 rounded-none"/>}
			{(status === 'error' || !src) && (<span className="absolute inset-0 flex items-center justify-center text-muted-foreground">
					<PersonIcon sx={{ fontSize: iconSize }}/>
				</span>)}
			{src && (<Img src={src} alt={alt} loading="lazy" onLoad={() => setStatus('loaded')} onError={() => setStatus('error')} className={cn('absolute inset-0 h-full w-full object-cover transition-opacity duration-300', status === 'loaded' ? 'opacity-100' : 'opacity-0')}/>)}
		</>);
};
// Multi-creator picker: platform pills over a typeahead, selections held as removable chips. Built on
// the same search-input + portaled-popover + chips shape as the redesign game pickers (GameMultiSelect)
// so the two read as one control family.
export const RedesignCreatorMultiSelect = ({ value, onChange, maxItems = 10, error }) => {
    var _a;
    const [platform, setPlatform] = useState(Platform.Twitch);
    const [query, setQuery] = useState('');
    const [focused, setFocused] = useState(false);
    const reduce = useReducedMotion();
    const platformOptions = useMemo(platformPillOptions, []);
    const debounced = useDebounce(query, debounceForPlatform(platform));
    const isFull = value.length >= maxItems;
    const creatorsQuery = useCreatorSearch(platform, debounced.trim(), {
        enabled: Boolean(debounced.trim().length) && !isFull,
    });
    const selectedKeys = new Set(value.map(creatorOptionKey));
    const options = ((_a = creatorsQuery.data) !== null && _a !== void 0 ? _a : []).filter(option => !selectedKeys.has(creatorOptionKey(option)));
    const open = focused && query.trim().length > 0 && !isFull
        && (creatorsQuery.isLoading || creatorsQuery.isError || options.length > 0 || Boolean(creatorsQuery.data));
    const add = (creator) => {
        if (isFull) {
            return;
        }
        onChange([...value, creator]);
        setQuery('');
    };
    const remove = (creator) => {
        onChange(value.filter(selected => creatorOptionKey(selected) !== creatorOptionKey(creator)));
    };
    return (<div className="space-y-3">
			<PillSelect value={platform} onChange={(next) => {
            setPlatform(next);
            setQuery('');
        }} options={platformOptions}/>

			<div>
				<motion.div layout className={cn('flex flex-wrap gap-2', value.length > 0 && 'mb-2')}>
					<AnimatePresence mode="popLayout" initial={false}>
						{value.map(creator => (<motion.span key={creatorOptionKey(creator)} layout initial={reduce ? false : { opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.8 }} transition={{ duration: 0.15, ease: 'easeOut' }} className="inline-flex items-center gap-1.5 rounded-full border border-solid border-white/[0.08] bg-white/[0.04] py-1 pl-1.5 pr-2 text-xs">
								<span className="relative inline-block h-5 w-5 shrink-0 overflow-hidden rounded-full bg-secondary">
									<CreatorAvatar src={creator.image} alt={creator.displayName} iconSize={11}/>
								</span>
								<span className="max-w-[160px] truncate">{creator.displayName}</span>
								<span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-white">
									<DarePlatformIcon size="9" platform={DARE_PLATFORM[creator.platform]} color={PlatformIconColor.Dark}/>
								</span>
								<button type="button" onClick={() => remove(creator)} className="text-muted-foreground transition-colors hover:text-destructive" aria-label={`Remove ${creator.displayName}`}>
									<CloseIcon sx={{ fontSize: 13 }}/>
								</button>
							</motion.span>))}
					</AnimatePresence>
				</motion.div>

				<Popover open={open} onOpenChange={() => undefined}>
					<PopoverAnchor asChild>
						<div className="relative">
							<SearchIcon sx={{ fontSize: 16 }} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
							<Input value={query} onChange={event => setQuery(event.target.value)} onFocus={() => setFocused(true)} onBlur={() => window.setTimeout(() => setFocused(false), 150)} placeholder={isFull ? `Up to ${maxItems} creators` : 'Search creators...'} disabled={isFull} error={error} className="pl-12 pr-9"/>
							{creatorsQuery.isLoading && !isFull && (<Spinner size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>)}
						</div>
					</PopoverAnchor>
					<PopoverContent align="start" sideOffset={6} onOpenAutoFocus={event => event.preventDefault()} className="max-h-60 w-[var(--radix-popover-trigger-width)] overflow-y-auto p-1">
						{creatorsQuery.isLoading && <p className="px-2 py-2 text-xs text-muted-foreground">Searching...</p>}
						{!creatorsQuery.isLoading && creatorsQuery.isError && (<p className="px-2 py-2 text-xs text-destructive">Creator search is unavailable right now.</p>)}
						{!creatorsQuery.isLoading && !creatorsQuery.isError && options.length === 0 && (<p className="px-2 py-2 text-xs text-muted-foreground">No creators found.</p>)}
						{!creatorsQuery.isLoading && !creatorsQuery.isError && options.map(creator => (<button key={creatorOptionKey(creator)} type="button" onMouseDown={event => event.preventDefault()} onClick={() => add(creator)} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-white/[0.06]">
								<span className="relative inline-block h-6 w-6 shrink-0 overflow-hidden rounded-full bg-secondary">
									<CreatorAvatar src={creator.image} alt={creator.displayName}/>
								</span>
								<span className="truncate">{creator.displayName}</span>
							</button>))}
					</PopoverContent>
				</Popover>
			</div>
		</div>);
};
